/* eslint-disable no-extra-semi */

/**
 * Utility functions for handling focus issues with DatePicker components in Material-UI v4 modals
 * This is a workaround for the focus trap issue where DatePicker's FocusTrap interferes with modal focus management
 */

/**
 * Checks if an element has a parent with the specified class name
 * @param {HTMLElement} el - The element to check
 * @param {string} classname - The class name to look for
 * @returns {boolean} - True if a parent with the class is found
 */
export const hasParentWithClass = (el, classname) => {
	let next = el.parentElement

	while (next !== null) {
		if (next.classList && next.classList.contains(classname)) {
			return true
		}
		next = next.parentElement
	}

	return false
}

/**
 * Handles click events in modals containing DatePicker components
 * This function blurs the active element if it's a DatePicker popper to prevent focus trap issues
 * @param {React.MouseEvent} e - The click event
 */
export const handlePickersModalClick = (e) => {
	// Check if the click target is not inside a DatePicker popper
	if (!hasParentWithClass(e.target, 'MuiPickersPopper-paper')) {
		// If the active element is a DatePicker popper, blur it
		if (document.activeElement && document.activeElement.classList.contains('MuiPickersPopper-paper')) {
			document.activeElement.blur()
		}
	}
}

/**
 * Enhanced version that also handles other common DatePicker focus trap scenarios
 * @param {React.MouseEvent} e - The click event
 */
export const handleDatePickerModalClick = (e) => {
	// Check if the click target is not inside any DatePicker related elements
	const isInsideDatePicker =
		hasParentWithClass(e.target, 'MuiPickersPopper-paper') ||
		hasParentWithClass(e.target, 'MuiPickersModal-dialog') ||
		hasParentWithClass(e.target, 'MuiPickersBasePicker-container')

	if (!isInsideDatePicker) {
		// If the active element is a DatePicker related element, blur it
		if (
			document.activeElement &&
			(document.activeElement.classList.contains('MuiPickersPopper-paper') ||
				document.activeElement.classList.contains('MuiPickersModal-dialog') ||
				document.activeElement.classList.contains('MuiPickersBasePicker-container'))
		) {
			document.activeElement.blur()
		}
	}
}

/**
 * Usage examples:
 *
 * 1. For DatePicker components:
 *    <DatePicker
 *      DialogProps={{
 *        disableBackdropClick: false,
 *        disableEscapeKeyDown: false,
 *        onClick: handleDatePickerModalClick
 *      }}
 *      // ... other props
 *    />
 *
 * 2. For modal/dialog content containing DatePickers:
 *    <DialogContent onClick={handleDatePickerModalClick}>
 *      <DatePicker />
 *    </DialogContent>
 *
 * 3. For any container with DatePickers:
 *    <div onClick={handleDatePickerModalClick}>
 *      <DatePicker />
 *    </div>
 */
